import { MAX_CHARACTER_INPUT_VALIDATION, MAX_TEXT_INPUT_VALIDATION, MAX_INTEGER } from "@/constants/common";
import { PATTERN } from "@/constants/pattern";
import { JOB_STATUS, TJobStatus } from "@/types/enum";
import { validator } from "@/utils/validator";
import { useMemo } from "react";

export const useGetJobSchema = () => {
  const workingTimeSchemaDraft = validator.object().shape({
    workingStartHour: validator.number().commas().emptyToNull().nullable().autoTransformToHalfWidth().integer().min(0).max(23).label("時").notRequired(),
    workingStartMinute: validator.number().commas().emptyToNull().nullable().autoTransformToHalfWidth().integer().min(0).max(59).label("分").notRequired(),
    workingEndHour: validator.number().commas().emptyToNull().nullable().autoTransformToHalfWidth().integer().min(0).max(23).label("時").notRequired(),
    workingEndMinute: validator.number().commas().emptyToNull().nullable().autoTransformToHalfWidth().integer().min(0).max(59).label("分").notRequired(),
  });

  const workingTimeSchemaRequired = validator.object().shape({
    workingStartHour: validator.number().commas().emptyToNull().autoTransformToHalfWidth().integer().min(0).max(23).label("時").required(),
    workingStartMinute: validator.number().commas().emptyToNull().autoTransformToHalfWidth().integer().min(0).max(59).label("分").required(),
    workingEndHour: validator.number().commas().emptyToNull().autoTransformToHalfWidth().integer().min(0).max(23).label("時").required(),
    workingEndMinute: validator.number().commas().emptyToNull().autoTransformToHalfWidth().integer().min(0).max(59).label("分").required(),
  });

  const jobSchema = useMemo(() => {
    return validator.object().shape({
      status: validator.string().isSelection().trim().label("ステータス").required(),
      tag: validator
        .array()
        .of(validator.string().trim().emptyToNull().required())
        .nullable()
        .label("タグ（優先ステータス）")
        .notRequired(),
      startDate: validator
        .mixed()
        .isDayjs()
        .isSelection()
        .nullable()
        .label("募集開始日")
        .isBeforeDateOfField("endDate", { startLabel: "募集開始日", endLabel: "募集終了日" })
        .notRequired(),
      endDate: validator
        .mixed()
        .isDayjs()
        .isSelection()
        .nullable()
        .label("募集終了日")
        .isAfterDateOfField("startDate", { startLabel: "募集開始日", endLabel: "募集終了日" })
        .notRequired(),
      statusMemo: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("ステータスメモ").notRequired(),

      companyId: validator.string().isSelection().trim().emptyToNull().label("企業名").required(),
      managementNumber: validator.string().trim().notComma().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("求人管理番号").notRequired(),
      jobName: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().label("求人名").required(),
      recruiterId: validator.number().isSelection().label("求人担当者").required(),
      companySpecificIndustryId: validator
        .number()
        .isSelection()
        .nullable()
        .label("分野")
        .when("status", {
          is: JOB_STATUS.DRAFT,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(),
        }),
      businessClassificationId: validator
        .number()
        .nullable()
        .label("取扱業務等の区分")
        .when("status", {
          is: JOB_STATUS.DRAFT,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(),
        }),
      companySpecificBusinessClassificationIds: validator
        .array()
        .of(validator.string().emptyToNull().required())
        .label("業務区分")
        .notRequired(),
      recruitmentNumber: validator
        .number()
        .commas()
        .nullable()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .integer()
        .min(0)
        .max(MAX_INTEGER)
        .label("募集人数")
        .when("status", {
          is: JOB_STATUS.DRAFT,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(),
        }),
      referralFee: validator.number().commas().emptyToNull().autoTransformToHalfWidth().nullable().integer().min(0).max(MAX_INTEGER).label("紹介料（売上）").notRequired(),
      referralFeeCost: validator.number().commas().emptyToNull().autoTransformToHalfWidth().nullable().integer().min(0).max(MAX_INTEGER).label("紹介料（仕入）").notRequired(),
      recruitmentSourceIds: validator
        .array()
        .of(validator.string().emptyToNull().required())
        .label("求人獲得ルート")
        .notRequired(),
      clientType: validator
        .array()
        .label("顧客区分")
        .notRequired(),
      recruiterNote: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("求人担当者コメント").notRequired(),

      businessContent: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("業務内容").notRequired(),
      employmentType: validator
        .string()
        .isSelection()
        .trim()
        .nullable()
        .emptyToNull()
        .label("雇用形態")
        .when("status", {
          is: JOB_STATUS.DRAFT,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(),
        }),
      salaryType: validator.string().isSelection().trim().emptyToNull().nullable().label("給与形態").notRequired(),
      baseSalary: validator
        .number()
        .commas()
        .nullable()
        .emptyToNull()
        .autoTransformToHalfWidth()
        .integer()
        .min(0)
        .max(MAX_INTEGER)
        .label("基本給")
        .when("status", {
          is: JOB_STATUS.DRAFT,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(),
        }),
      totalAmount: validator.number().commas().emptyToNull().autoTransformToHalfWidth().nullable().integer().min(0).max(MAX_INTEGER).label("総支給額").notRequired(),
      totalAmountBreakdown: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("総支給額の内訳").notRequired(),
      netSalaryEstimate: validator.number().commas().emptyToNull().autoTransformToHalfWidth().nullable().integer().min(0).max(MAX_INTEGER).label("目安手取り金額").notRequired(),
      salaryDeductions: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("給与控除").notRequired(),
      hourlyWage: validator.number().commas().emptyToNull().autoTransformToHalfWidth().nullable().integer().min(0).max(MAX_INTEGER).label("1時間あたりの給与").notRequired(),
      otherAllowanceAmount: validator.number().commas().emptyToNull().autoTransformToHalfWidth().nullable().integer().min(0).max(MAX_INTEGER).label("その他手当の金額").notRequired(),
      otherAllowanceDescription: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("その他手当の内容").notRequired(),
      overtimeDescription: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("残業について").notRequired(),
      bonus: validator
        .string()
        .isSelection()
        .trim()
        .emptyToNull()
        .nullable()
        .label("賞与")
        .when("status", {
          is: JOB_STATUS.DRAFT,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(),
        }),
      bonusInfo: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("賞与時期・金額"),
      salaryRaise: validator
        .string()
        .isSelection()
        .trim()
        .nullable()
        .emptyToNull()
        .label("昇給")
        .when("status", {
          is: JOB_STATUS.DRAFT,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(),
        }),
      salaryRaiseTime: validator.string().max(MAX_CHARACTER_INPUT_VALIDATION).nullable().label("昇給時期"),

      workplace: validator
        .string()
        .trim()
        .max(MAX_CHARACTER_INPUT_VALIDATION)
        .nullable()
        .emptyToNull()
        .label("勤務場所")
        .when("status", {
          is: JOB_STATUS.DRAFT,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(),
        }),
      workplaceDetail: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("勤務地（詳細）").notRequired(),
      jobWorkingTimes: validator
        .array()
        .notRequired()
        .when("status", {
          is: (value: TJobStatus) => value === JOB_STATUS.DRAFT,
          then: () => validator.array().of(workingTimeSchemaDraft),
          otherwise: () => validator.array().of(workingTimeSchemaRequired).required(),
        }),
      regularHolidays: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("休日（定例日）").notRequired(),
      irregularHolidays: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("休日（非定例日）").notRequired(),
      totalAnnualHolidays: validator.number().commas().emptyToNull().nullable().autoTransformToHalfWidth().min(0).max(365).label("年間合計休日日数").notRequired(),

      japaneseLevelType: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).nullable().label("必要な日本語レベル").notRequired(),
      japaneseLevelOther: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).nullable().label("必要な日本語レベル（その他）").notRequired(),
      skillsExperience: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).nullable().label("必要な技能・経験").notRequired(),
      otherApplicationRequirements: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).nullable().label("その他応募要件").notRequired(),
      ageLimit: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).nullable().label("年齢制限（上限）").notRequired(),
      nationalityIds: validator
        .array()
        .of(validator.string().emptyToNull().required())
        .nullable()
        .label("国籍")
        .when("status", {
          is: JOB_STATUS.DRAFT,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.min(1, "「国籍」を選択してください。").required(),
        }),
      recruitmentLocationType: validator
        .string()
        .isSelection()
        .trim()
        .nullable()
        .label("募集地")
        .when("status", {
          is: JOB_STATUS.DRAFT,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(),
        }),
      desiredHiringPeriod: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("採用希望時期"),
      gender: validator.string().trim().emptyToNull().nullable().label("性別"),
      residenceStatusIds: validator
        .array()
        .of(validator.string().emptyToNull().required())
        .nullable()
        .label("在留資格")
        .when("status", {
          is: JOB_STATUS.DRAFT,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) => schema.required(),
        }),
      specifiedSkillPassed: validator
        .array(validator.string().trim().emptyToNull().required())
        .label("特定技能試験合格")
        .notRequired(),
      jobDocumentUrl: validator.string().trim().emptyToNull().matches(PATTERN.CUSTOM_URL, "正しいURL形式で入力してください。").max(MAX_TEXT_INPUT_VALIDATION).nullable().label("求人票"),
      selectionProcess: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("選考フロー").notRequired(),

      companyAccommodation: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("社宅・住居情報").notRequired(),
      specifiedSkillNo2: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("特定技能2号支援").notRequired(),
      otherBenefits: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("その他福利厚生").notRequired(),
      insuranceStatus: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("社会保険・雇用保険の加入状況").notRequired(),

      memo: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("特性メモ").notRequired(),
      jobMemo: validator.string().trim().emptyToNull().nullable().max(MAX_TEXT_INPUT_VALIDATION).label("求人特性メモ").notRequired(),
      foreignEmployees: validator.string().trim().emptyToNull().nullable().max(MAX_TEXT_INPUT_VALIDATION).label("在籍外国人").notRequired(),
      onboardingSupport: validator
        .array()
        .of(
          validator.object({
            label: validator
              .string()
              .trim()
              .emptyToNull()
              .max(MAX_CHARACTER_INPUT_VALIDATION)
              .required()
              .label("受け入れ体制・サポート"),
            checked: validator.boolean().required(),
          })
        )
        .nullable()
        .notRequired(),

      relatedFileTitles: validator.array().of(validator.string().trim().emptyToNull().required().label("付与する書類名").max(MAX_CHARACTER_INPUT_VALIDATION)).notRequired(),
    });
  }, []);

  return { jobSchema }
}
